import React, {
	useCallback,
	useContext,
	memo,
	useMemo,
	useRef,
	useEffect,
} from "react";
import clsx from "clsx";
import fp from "lodash/fp";
import { Form, Col, Row } from "react-bootstrap";

import {
	Skeleton,
	Tooltip,
	RequiredMark,
	OverlayTrigger,
	Button,
} from "components";
import {
	ApplicationEntryProgressContext,
	BrandingColorContext,
	RequiredMarkContext,
} from "components/contexts";
import { generateAutotestValue } from "helpers";

import ReadOnlyInput from "./readOnlyInput";

import styles from "./formField.module.scss";

const ONLY_BORDER_ERROR = "onlyBorderError";

const CustomInput = ({
	input,
	as,
	label,
	helperInput,
	type,
	placeholder,
	meta: { touched, error, warning, asyncValidating },
	disabled,
	disableInput,
	commonName,
	smLabel = 6,
	smSelect = 6,
	tabIndex,
	normalizeOnBlur,
	loader,
	loading, // Some operation being proceed. E.g. custom async validation
	blurLoading, // Display blur instead of the input field
	customError,
	wrapperClassName,
	wrapperCustomInput,
	classNameInput,
	labelClassName,
	hideRequiredMark, //works only with RequiredMarkContext
	readOnly,
	autoTest = "",
	placeholderClassName = null,
	disableTriggerSave,
	solveCaretIssue,
	warningTitleTooltip,
	warningIconTooltip,
	maxHeight = false,
	role = false,
	requiredMark = false,
	isShowWarningTooltip = false,
	isPlainText = false,
	labelIcon,
	labelIconTooltip = "",
	inputIcon,
	inputIconMessage,
	...props
}) => {
	const oldText = useRef("");

	useEffect(() => {
		//set previous value
		formatValueFirstLoad();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const formatValueAfterSave = useCallback(
		(input, solveCaretIssue) => {
			const minimunLengthToStartNormalize = placeholder
				? placeholder.length + 3
				: 6;
			const parseVal = input?.value?.toString();
			if (
				parseVal &&
				solveCaretIssue &&
				parseVal?.indexOf(",") === -1 &&
				parseVal?.length > minimunLengthToStartNormalize
			) {
				let result;
				if (normalizeOnBlur) {
					result = normalizeOnBlur(solveCaretIssue(parseVal));
				} else {
					result = solveCaretIssue(parseVal);
				}
				oldText.current = result;
				input.onChange(result);
			}
		},
		[normalizeOnBlur, placeholder],
	);

	useEffect(() => {
		formatValueAfterSave(input, solveCaretIssue);
	}, [input, solveCaretIssue, loading, formatValueAfterSave]);

	const formatValueFirstLoad = () => {
		const parseVal = input?.value?.toString();
		if (
			parseVal &&
			solveCaretIssue &&
			normalizeOnBlur &&
			(placeholder === "0.00" || placeholder === "0.000000")
		) {
			const result = normalizeOnBlur(solveCaretIssue(parseVal));
			input.onChange(result);

			oldText.current = result;
		} else {
			oldText.current = input.value;
		}
	};

	const showRequiredMark =
		(useContext(RequiredMarkContext) && !hideRequiredMark && !disabled) ||
		requiredMark;
	const brandingColor =
		useContext(BrandingColorContext) || props.brandingColor;
	const { triggerSave } = useContext(ApplicationEntryProgressContext);

	const disabledClass = disabled && styles.disabled;

	const labelBlock = label && (
		<Form.Label
			className={`${styles.wrappLabel} ${labelClassName}`}
			column
			sm={smLabel}
		>
			{label}
			{showRequiredMark && <RequiredMark />}
			{!!labelIcon && (
				<div className={styles.labelIcon}>
					<Tooltip
						html={
							<div className={styles.labelTitleWrapper}>
								{labelIconTooltip}
							</div>
						}
						disabled={!labelIconTooltip}
					>
						{labelIcon}
					</Tooltip>
				</div>
			)}
		</Form.Label>
	);

	const showError = !disabled && !disableInput && !blurLoading;
	const errorMessage = useMemo(
		() => error && <p className={styles.spanError}>{error}</p>,
		[error],
	);
	const customErrorMessage = customError && (
		<p className="errorMessage">{customError}</p>
	);
	const warningMessage = warning && (
		<p className={styles.spanWarn}>{warning}</p>
	);

	const errorText =
		(error !== ONLY_BORDER_ERROR &&
			showError &&
			touched &&
			(errorMessage || customErrorMessage)) ||
		warningMessage;

	const validationClass =
		showError &&
		touched &&
		(((error || customError) && styles.error) ||
			(warning && styles.warning));

	const onBlurHandler = useCallback(
		event => {
			if (normalizeOnBlur) {
				event.target.value = normalizeOnBlur(event.target.value);
			}
			// Original onblur
			input.onBlur(event);
			if (disableTriggerSave) {
				return;
			} else {
				triggerSave?.();
			}
		},
		[input, normalizeOnBlur, triggerSave, disableTriggerSave],
	);

	const runAfterUpdate = useRunAfterUpdate();

	function useRunAfterUpdate() {
		const afterPaintRef = React.useRef(null);
		React.useLayoutEffect(() => {
			if (afterPaintRef.current) {
				afterPaintRef.current();
				afterPaintRef.current = null;
			}
		});
		return fn => (afterPaintRef.current = fn);
	}

	const onChange = useCallback(
		event => {
			if (solveCaretIssue) {
				const text = event.target;
				const result = solveCaretIssue(text.value);
				const diff = result.length - oldText.current.length;
				var start;
				if (diff > 0) {
					start = text.selectionStart + diff - 1;
				} else if (diff === 0) {
					start = text.selectionStart + 1;
				} else if (text.selectionStart === 0 && diff === -2) {
					start = text.selectionStart;
				} else {
					start = text.selectionStart + 1 + diff;
				}

				oldText.current = result;
				if (input) {
					input?.onChange(event);
				}
				runAfterUpdate(() => {
					text.selectionStart = start;
					text.selectionEnd = start;
				});
				return;
			}
			input.onChange(event);
		},
		[input, runAfterUpdate, solveCaretIssue],
	);

	const onFocusHandler = useCallback(
		event => {
			// Original onblur
			input.onFocus(event);
			if (disableTriggerSave) {
				return;
			} else {
				triggerSave?.();
			}
		},
		[input, triggerSave, disableTriggerSave],
	);

	let checkRole = role && styles.merhantDesc;
	let checkHeight = maxHeight && styles.maxHeight;
	const isBrandingColor = brandingColor && styles.brandingColor;
	const inputComponent = useMemo(
		() =>
			fp.cond([
				[() => blurLoading, () => <Skeleton height="34px" />],
				[
					() => readOnly,
					() => (
						<ReadOnlyInput
							name={input.name}
							value={input.value}
							isPlainText={isPlainText}
							disabled={disabled || disableInput}
						/>
					),
				],
				[
					fp.T,
					() => (
						<>
							<Form.Control
								{...props}
								{...input}
								as={as}
								onBlur={onBlurHandler}
								onFocus={onFocusHandler}
								onChange={onChange}
								tabIndex={tabIndex}
								common-name={commonName}
								placeholder={placeholder}
								disabled={disabled || disableInput}
								type={type}
								isInvalid={touched && errorMessage}
								className={clsx(
									styles.field,
									checkRole,
									validationClass,
									classNameInput,
									checkHeight,
									isBrandingColor,
									placeholderClassName &&
										!disabled &&
										placeholderClassName,
								)}
								data-autotest={`input${generateAutotestValue(
									autoTest,
									input.name,
								)}`}
							/>
							{inputIcon && (
								<div className={styles.inputIconHolder}>
									<Tooltip
										html={inputIconMessage}
										disabled={false}
									>
										<Button
											onClick={() => {}}
											icon={inputIcon}
											btnIcon
											variant="secondary"
											title=""
											disabled={disabled}
										/>
									</Tooltip>
								</div>
							)}
							{errorText}
							{helperInput && (
								<small className={styles.helperInput}>
									{helperInput}
								</small>
							)}
						</>
					),
				],
			])(null),
		[
			blurLoading,
			readOnly,
			input,
			isPlainText,
			disabled,
			disableInput,
			props,
			as,
			onBlurHandler,
			onFocusHandler,
			onChange,
			tabIndex,
			commonName,
			placeholder,
			type,
			touched,
			errorMessage,
			checkRole,
			validationClass,
			classNameInput,
			checkHeight,
			isBrandingColor,
			placeholderClassName,
			autoTest,
			inputIcon,
			inputIconMessage,
			errorText,
			helperInput,
		],
	);

	return (
		<div
			className={clsx(
				disabledClass,
				styles.formGroup,
				wrapperCustomInput,
			)}
		>
			<Form.Group
				as={Row}
				className={clsx(styles.inputWrapper, wrapperClassName)}
			>
				{labelBlock}
				<Col sm={smSelect}>{inputComponent}</Col>
				{!blurLoading && (loading || asyncValidating) && loader?.()}
				{isShowWarningTooltip && (
					<div className={styles.warningContainer}>
						<OverlayTrigger
							title={warningTitleTooltip}
							tooltipClassName={styles.tooltip}
							overlayIcon={warningIconTooltip}
						/>
					</div>
				)}
			</Form.Group>
		</div>
	);
};

export default memo(CustomInput);
